import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Sparkles, Users, EyeOff, ArrowRight } from "lucide-react";
import MarketingHeader from "@/components/MarketingHeader";
import MarketingFooter from "@/components/MarketingFooter";
import { useI18n } from "@/contexts/LanguageContext";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const HERO_IMG =
  "https://images.unsplash.com/photo-1621621668101-d5c8329b3784?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxzb3BoaXN0aWNhdGVkJTIwcm9tYW50aWMlMjBjb3VwbGUlMjBlbGVnYW50fGVufDB8fHx8MTc4NjExOTQ4N3ww&ixlib=rb-4.1.0&q=85";
const COMPAT_IMG =
  "https://images.unsplash.com/photo-1544005313-94ddf0286df2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxlbGVnYW50JTIwYXR0cmFjdGl2ZSUyMGFkdWx0JTIwcG9ydHJhaXQlMjBzdW5zZXR8ZW58MHx8fHwxNzg2MTE5NTAwfDA&ixlib=rb-4.1.0&q=85";
const COMMUNITY_IMG =
  "https://images.pexels.com/photos/29843477/pexels-photo-29843477.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const TRUST_IMG =
  "https://images.unsplash.com/photo-1634595477722-7bc68dd410fd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxlbGVnYW50JTIwYXR0cmFjdGl2ZSUyMGFkdWx0JTIwcG9ydHJhaXQlMjBzdW5zZXR8ZW58MHx8fHwxNzg2MTE5NTAwfDA&ixlib=rb-4.1.0&q=85";

function SectionOverline({ text }) {
  return (
    <p className="nvl-overline" style={{ color: "hsl(var(--nvl-accent))" }}>
      {text}
    </p>
  );
}

function Section({ id, children, className = "" }) {
  return (
    <section id={id} className={`py-20 md:py-28 ${className}`} data-testid={`section-${id}`}>
      <div className="nvl-container">{children}</div>
    </section>
  );
}

export default function Landing() {
  const { t } = useI18n();

  return (
    <div className="min-h-screen flex flex-col">
      <MarketingHeader />

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="section-hero">
        <div className="nvl-container pt-16 md:pt-24 pb-20 md:pb-28 grid gap-12 md:grid-cols-2 items-center">
          <div>
            <SectionOverline text={t("landing.hero.overline")} />
            <h1 className="mt-4 font-display text-5xl md:text-6xl lg:text-7xl leading-[1.02]">
              {t("landing.hero.title")}
            </h1>
            <p className="mt-6 text-base md:text-lg max-w-xl" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("landing.hero.subtitle")}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-cta-register" className="nvl-btn nvl-btn-primary">
                {t("common.joinCta")} <ArrowRight size={16} className="ml-2" />
              </Link>
              <a href="#how" className="nvl-btn nvl-btn-outline" data-testid="hero-cta-discover">
                {t("common.discoverCta")}
              </a>
            </div>
            <p className="mt-8 text-xs uppercase tracking-[0.22em]" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("common.slogan")}
            </p>
          </div>
          <div className="relative">
            <div
              className="relative aspect-[4/5] overflow-hidden rounded-md"
              style={{ border: "1px solid hsl(var(--nvl-border))" }}
            >
              <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(10,17,40,0) 40%, rgba(10,17,40,0.35) 100%)",
                }}
              />
            </div>
            <div
              className="hidden md:block absolute -bottom-6 -left-6 max-w-xs nvl-card p-4"
              style={{ backgroundColor: "hsl(var(--nvl-surface))" }}
            >
              <p className="nvl-overline" style={{ color: "hsl(var(--nvl-accent))" }}>
                Europe
              </p>
              <p className="mt-2 font-display text-lg leading-snug">
                Italiano · English · más idiomas próximamente.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What */}
      <Section id="what">
        <div className="grid gap-10 md:grid-cols-12 items-end">
          <div className="md:col-span-5">
            <SectionOverline text={t("landing.what.overline")} />
            <h2 className="mt-3 font-display text-4xl md:text-5xl leading-tight">
              {t("landing.what.title")}
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <p className="text-base md:text-lg" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("landing.what.body")}
            </p>
          </div>
        </div>
      </Section>

      {/* Why */}
      <Section id="why">
        <SectionOverline text={t("landing.why.overline")} />
        <h2 className="mt-3 font-display text-4xl md:text-5xl max-w-2xl leading-tight">
          {t("landing.why.title")}
        </h2>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {t("landing.why.items").map((it, i) => (
            <div key={i} className="nvl-card p-6" data-testid={`why-card-${i}`}>
              <div
                className="w-9 h-9 rounded-md flex items-center justify-center"
                style={{ backgroundColor: "hsl(var(--nvl-ink))", color: "hsl(var(--nvl-bg))" }}
              >
                <Sparkles size={16} />
              </div>
              <h3 className="mt-5 font-display text-2xl">{it.title}</h3>
              <p className="mt-2 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
                {it.body}
              </p>
            </div>
          ))}
        </div>
      </Section>

      {/* Compatibility */}
      <Section id="compatibility">
        <div className="grid gap-12 md:grid-cols-2 items-center">
          <div className="order-2 md:order-1">
            <div
              className="aspect-[5/6] overflow-hidden rounded-md"
              style={{ border: "1px solid hsl(var(--nvl-border))" }}
            >
              <img src={COMPAT_IMG} alt="" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="order-1 md:order-2">
            <SectionOverline text={t("landing.compatibility.overline")} />
            <h2 className="mt-3 font-display text-4xl md:text-5xl leading-tight">
              {t("landing.compatibility.title")}
            </h2>
            <p className="mt-6 max-w-lg" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("landing.compatibility.body")}
            </p>
          </div>
        </div>
      </Section>

      {/* Membership */}
      <Section id="membership">
        <div
          className="rounded-md p-10 md:p-16 grid gap-8 md:grid-cols-2 items-center"
          style={{ backgroundColor: "hsl(var(--nvl-ink))", color: "hsl(var(--nvl-bg))" }}
        >
          <div>
            <p className="nvl-overline opacity-70">{t("landing.membership.overline")}</p>
            <h2 className="mt-3 font-display text-4xl md:text-5xl leading-tight">
              {t("landing.membership.title")}
            </h2>
          </div>
          <div>
            <p className="opacity-85">{t("landing.membership.body")}</p>
            <Link
              to="/register"
              data-testid="membership-cta-register"
              className="mt-6 inline-flex items-center nvl-btn"
              style={{ backgroundColor: "hsl(var(--nvl-accent))", color: "hsl(var(--nvl-bg))" }}
            >
              {t("landing.membership.cta")} <ArrowRight size={16} className="ml-2" />
            </Link>
          </div>
        </div>
      </Section>

      {/* Trust */}
      <Section id="trust">
        <div className="grid gap-12 md:grid-cols-2 items-center">
          <div>
            <SectionOverline text={t("landing.trust.overline")} />
            <h2 className="mt-3 font-display text-4xl md:text-5xl leading-tight">
              {t("landing.trust.title")}
            </h2>
            <p className="mt-6 max-w-lg" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("landing.trust.body")}
            </p>
            <div className="mt-8 flex items-center gap-3 text-sm">
              <ShieldCheck size={18} style={{ color: "hsl(var(--nvl-accent))" }} />
              <span>Manual review · Active moderation · Report tools</span>
            </div>
          </div>
          <div
            className="aspect-[5/6] overflow-hidden rounded-md"
            style={{ border: "1px solid hsl(var(--nvl-border))" }}
          >
            <img src={TRUST_IMG} alt="" className="w-full h-full object-cover" />
          </div>
        </div>
      </Section>

      {/* Privacy */}
      <Section id="privacy">
        <div className="grid gap-8 md:grid-cols-3">
          <div className="md:col-span-1">
            <SectionOverline text={t("landing.privacy.overline")} />
            <h2 className="mt-3 font-display text-4xl md:text-5xl leading-tight">
              {t("landing.privacy.title")}
            </h2>
          </div>
          <div className="md:col-span-2 grid gap-6 sm:grid-cols-2">
            <div className="nvl-card p-6">
              <EyeOff size={18} style={{ color: "hsl(var(--nvl-accent))" }} />
              <h3 className="mt-4 font-display text-2xl">Approximate location</h3>
              <p className="mt-2 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
                City & distance only — never exact GPS.
              </p>
            </div>
            <div className="nvl-card p-6">
              <ShieldCheck size={18} style={{ color: "hsl(var(--nvl-accent))" }} />
              <h3 className="mt-4 font-display text-2xl">Encrypted data</h3>
              <p className="mt-2 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
                Sensitive fields are never exposed to other users.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* How */}
      <Section id="how">
        <SectionOverline text={t("landing.how.overline")} />
        <h2 className="mt-3 font-display text-4xl md:text-5xl max-w-2xl leading-tight">
          {t("landing.how.title")}
        </h2>
        <div className="mt-12 grid gap-8 md:grid-cols-3">
          {t("landing.how.steps").map((s, i) => (
            <div key={i} className="border-t pt-6" style={{ borderColor: "hsl(var(--nvl-ink))" }}>
              <div className="font-display text-5xl" style={{ color: "hsl(var(--nvl-accent))" }}>
                {String(i + 1).padStart(2, "0")}
              </div>
              <h3 className="mt-4 font-display text-2xl">{s.title}</h3>
              <p className="mt-2 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
                {s.body}
              </p>
            </div>
          ))}
        </div>
      </Section>

      {/* Community philosophy */}
      <Section id="philosophy">
        <div className="grid gap-12 md:grid-cols-2 items-center">
          <div
            className="aspect-[5/6] overflow-hidden rounded-md"
            style={{ border: "1px solid hsl(var(--nvl-border))" }}
          >
            <img src={COMMUNITY_IMG} alt="" className="w-full h-full object-cover" />
          </div>
          <div>
            <SectionOverline text={t("landing.community.overline")} />
            <h2 className="mt-3 font-display text-4xl md:text-5xl leading-tight">
              {t("landing.community.title")}
            </h2>
            <p className="mt-6 max-w-lg" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("landing.community.body")}
            </p>
            <div className="mt-6 flex items-center gap-2 text-sm">
              <Users size={18} style={{ color: "hsl(var(--nvl-accent))" }} />
              <span>Everyone aligned with our values is welcome.</span>
            </div>
          </div>
        </div>
      </Section>

      {/* FAQ */}
      <Section id="faq">
        <SectionOverline text={t("landing.faq.overline")} />
        <h2 className="mt-3 font-display text-4xl md:text-5xl leading-tight">
          {t("landing.faq.title")}
        </h2>
        <div className="mt-10 max-w-3xl">
          <Accordion type="single" collapsible data-testid="faq-accordion">
            {t("landing.faq.items").map((item, i) => (
              <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left font-display text-xl md:text-2xl">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm md:text-base" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Section>

      {/* Final CTA */}
      <Section id="final-cta">
        <div
          className="rounded-md p-12 md:p-20 text-center"
          style={{ backgroundColor: "hsl(var(--nvl-ink))", color: "hsl(var(--nvl-bg))" }}
        >
          <p className="nvl-overline opacity-70">{t("landing.finalCta.overline")}</p>
          <h2 className="mt-4 font-display text-4xl md:text-6xl max-w-3xl mx-auto leading-tight">
            {t("landing.finalCta.title")}
          </h2>
          <p className="mt-6 opacity-85 max-w-xl mx-auto">{t("landing.finalCta.body")}</p>
          <Link
            to="/register"
            data-testid="final-cta-register"
            className="mt-10 inline-flex items-center nvl-btn"
            style={{ backgroundColor: "hsl(var(--nvl-accent))", color: "hsl(var(--nvl-bg))" }}
          >
            {t("landing.finalCta.cta")} <ArrowRight size={16} className="ml-2" />
          </Link>
        </div>
      </Section>

      <MarketingFooter />
    </div>
  );
}
